import zod from "zod";

/**
 * The languages the portal is translated into. English is the source: every other locale has a
 * catalog in packages/app/messages/<locale>.json keyed like messages/en.json. Public pages, docs and
 * the blog stay in English.
 */
export const APP_LOCALES = [
  "en",
  "ar",
  "de",
  "es",
  "fr",
  "hi",
  "id",
  "it",
  "ja",
  "ko",
  "nl",
  "pl",
  "pt-BR",
  "ru",
  "tr",
  "zh-CN",
] as const;
export type AppLocale = (typeof APP_LOCALES)[number];
export const LOCALE_SCHEMA = zod.enum(APP_LOCALES);

export const DEFAULT_LOCALE: AppLocale = "en";

/** Locales written right to left. */
const RTL_LOCALES: ReadonlySet<AppLocale> = new Set(["ar"]);
export const localeDirection = (locale: AppLocale): "ltr" | "rtl" =>
  RTL_LOCALES.has(locale) ? "rtl" : "ltr";

/** Each locale's name in its own language, for the language picker. */
export const LOCALE_NAMES: Record<AppLocale, string> = {
  en: "English",
  ar: "العربية",
  de: "Deutsch",
  es: "Español",
  fr: "Français",
  hi: "हिन्दी",
  id: "Bahasa Indonesia",
  it: "Italiano",
  ja: "日本語",
  ko: "한국어",
  nl: "Nederlands",
  pl: "Polski",
  "pt-BR": "Português (Brasil)",
  ru: "Русский",
  tr: "Türkçe",
  "zh-CN": "简体中文",
};

/** The supported locale for one BCP 47 tag: exact, then by language (`pt-PT` → `pt-BR`). */
export const matchLocale = (tag: string): AppLocale | undefined => {
  const normalised = tag.trim().toLowerCase();
  if (!normalised) return undefined;
  const exact = APP_LOCALES.find((locale) => locale.toLowerCase() === normalised);
  if (exact) return exact;
  const language = normalised.split("-")[0];
  // Traditional Chinese readers aren't served by the Simplified catalog.
  if (language === "zh" && /-(hant|tw|hk|mo)\b/.test(normalised)) return undefined;
  return APP_LOCALES.find((locale) => locale.toLowerCase().split("-")[0] === language);
};

/**
 * The best supported locale for an `Accept-Language` header (or the browser's `navigator.languages`
 * joined with commas), by quality then order, else English.
 */
export const negotiateLocale = (acceptLanguage: string | null | undefined): AppLocale => {
  const ranked = (acceptLanguage ?? "")
    .split(",")
    .map((entry, index) => {
      const [tag = "", ...params] = entry.split(";");
      const quality = params.map((param) => /^\s*q=([\d.]+)\s*$/.exec(param)?.[1]).find(Boolean);
      return { tag, quality: quality === undefined ? 1 : Number(quality), index };
    })
    .filter(({ tag, quality }) => tag.trim() && tag.trim() !== "*" && quality > 0)
    .sort((a, b) => b.quality - a.quality || a.index - b.index);
  for (const { tag } of ranked) {
    const locale = matchLocale(tag);
    if (locale) return locale;
  }
  return DEFAULT_LOCALE;
};
